import { BUILTIN_DASHBOARDS, BUILTIN_REPORTS, STORAGE_KEY } from "./catalog";
import { defaultReportingState } from "./nclean-catalog";
import {
  deleteDefinition,
  type DataBindingDefinition,
  type GeneratedReport,
  type ReportingState,
  type WidgetDefinition,
} from "./reporting";
import type { DashboardDefinition, QueryDefinition } from "./types";

const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const named = (value: unknown): value is Record<string, unknown> =>
  record(value) &&
  typeof value.id === "string" &&
  typeof value.name === "string";
const parameters = (value: unknown) =>
  Array.isArray(value) &&
  value.every(
    (p) =>
      record(p) &&
      typeof p.name === "string" &&
      typeof p.label === "string" &&
      ["date", "string", "number", "boolean"].includes(String(p.type)) &&
      (p.type === "date"
        ? typeof p.default === "string"
        : typeof p.default === p.type),
  );
function validateV3(state: ReportingState) {
  const common = (value: unknown) =>
    named(value) && typeof value.description === "string";
  if (
    !state.widgets.every(
      (w) =>
        common(w) &&
        Array.isArray(w.inputs) &&
        w.inputs.every(
          (i) =>
            record(i) &&
            typeof i.name === "string" &&
            [
              "number",
              "string",
              "boolean",
              "date",
              "timestamp",
              "array",
              "record",
            ].includes(i.type) &&
            typeof i.nullable === "boolean",
        ) &&
        record(w.visualization) &&
        ["kpi", "table", "bar", "line", "pie"].includes(w.visualization.type),
    ) ||
    !state.bindings.every(
      (b) =>
        named(b) &&
        parameters(b.parameters) &&
        record(b.inputMapping) &&
        record(b.query) &&
        (b.query.mode === "malloy"
          ? typeof b.query.malloy === "string"
          : b.query.mode === "guided" && record(b.query.query)),
    ) ||
    !state.templates.every(
      (t) =>
        common(t) &&
        parameters(t.parameters) &&
        Array.isArray(t.bindingIds) &&
        typeof t.sourceAt === "string" &&
        Array.isArray(t.sections) &&
        t.sections.every(
          (s) =>
            record(s) &&
            typeof s.id === "string" &&
            typeof s.title === "string" &&
            (s.kind === "text"
              ? typeof s.text === "string"
              : s.kind === "widget" &&
                typeof s.widgetId === "string" &&
                typeof s.bindingId === "string"),
        ),
    ) ||
    !state.dashboards.every(
      (d) =>
        common(d) &&
        parameters(d.parameters) &&
        typeof d.sourceAt === "string" &&
        Array.isArray(d.widgets) &&
        d.widgets.every(
          (w) =>
            record(w) &&
            typeof w.id === "string" &&
            typeof w.widgetId === "string" &&
            typeof w.bindingId === "string",
        ),
    ) ||
    !state.deletedBuiltins.every((id) => typeof id === "string")
  )
    throw new Error(
      "Saved definitions contain an invalid entry. Use Restore examples to recover the default definitions; the existing storage has not been overwritten.",
    );
}

function migrateLegacy(candidate: {
  reports: QueryDefinition[];
  dashboards: (DashboardDefinition & { reportIds?: string[] })[];
}): ReportingState {
  const state = defaultReportingState();
  const dashboards = candidate.dashboards.filter((d) => {
    const builtin = BUILTIN_DASHBOARDS.find((b) => b.id === d.id);
    return !builtin || JSON.stringify(d) !== JSON.stringify(builtin);
  });
  const referenced = new Set(
    dashboards.flatMap(
      (d) => d.widgets?.map((w) => w.reportId) ?? d.reportIds ?? [],
    ),
  );
  const reports = candidate.reports.filter((r) => {
    const builtin = BUILTIN_REPORTS.find((b) => b.id === r.id);
    return (
      referenced.has(r.id) ||
      !builtin ||
      JSON.stringify(r) !== JSON.stringify(builtin)
    );
  });
  for (const r of reports) {
    if (r.mode !== "malloy" && r.mode !== "guided")
      throw new Error(
        "An existing saved query has an unsupported format. Its backup has been preserved.",
      );
    const widget: WidgetDefinition = {
      id: `legacy-widget-${r.id}`,
      name: r.name,
      description: r.description,
      revision: 1,
      inputs: [{ name: "rows", type: "array", nullable: false }],
      visualization: structuredClone(r.visualization),
    };
    const binding: DataBindingDefinition = {
      id: `legacy-binding-${r.id}`,
      name: r.name,
      revision: 1,
      parameters: [],
      inputMapping: { rows: "$rows" },
      query:
        r.mode === "guided"
          ? { mode: "guided", query: r.query }
          : { mode: "malloy", malloy: r.malloy, guidedDraft: r.guidedDraft },
    };
    state.widgets.push(widget);
    state.bindings.push(binding);
  }
  for (const d of dashboards) {
    const placements =
      d.widgets ??
      (d.reportIds ?? []).map((reportId, i) => ({
        id: `${d.id}-${i}`,
        reportId,
        width: "half" as const,
      }));
    state.dashboards.push({
      id: `legacy-${d.id}`,
      name: d.name,
      description: d.description,
      parameters: [],
      sourceAt: "2026-10-06T13:20:00+03:00",
      widgets: placements.map(({ reportId, ...placement }) => ({
        ...placement,
        widgetId: `legacy-widget-${reportId}`,
        bindingId: `legacy-binding-${reportId}`,
      })),
    });
  }
  return state;
}

export function readReportingState(
  storage: Pick<Storage, "getItem" | "setItem"> = localStorage,
): ReportingState {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return defaultReportingState();
  const candidate = JSON.parse(raw);
  if (candidate.version === 3) {
    if (
      ![
        "widgets",
        "bindings",
        "templates",
        "dashboards",
        "deletedBuiltins",
      ].every((key) => Array.isArray(candidate[key]))
    )
      throw new Error(
        "Saved definitions cannot be read. Use Restore examples to recover the default definitions.",
      );
    const state = candidate as ReportingState,
      defaults = defaultReportingState();
    validateV3(state);
    // Remove retired metadata from existing definitions without resetting custom work.
    for (const definition of [...state.templates, ...state.dashboards]) {
      delete (definition as unknown as Record<string, unknown>).assumptions;
    }
    for (const binding of state.bindings) {
      if (binding.query.mode === "malloy") {
        binding.query.malloy = binding.query.malloy.replaceAll(
          ", services.provenance",
          "",
        );
      }
    }
    for (const kind of [
      "widgets",
      "bindings",
      "templates",
      "dashboards",
    ] as const) {
      const items = state[kind] as { id: string }[];
      items.push(
        ...defaults[kind].filter(
          (item) =>
            !items.some((i) => i.id === item.id) &&
            !state.deletedBuiltins.includes(`${kind}:${item.id}`),
        ),
      );
    }
    return state;
  }
  if (
    ![1, 2].includes(candidate.version) ||
    !Array.isArray(candidate.reports) ||
    !Array.isArray(candidate.dashboards)
  )
    throw new Error(
      "Unknown saved definitions. Use Restore examples to recover the default definitions.",
    );
  // Back up the actual legacy payload before model-version checks or migration.
  if (!storage.getItem(`${STORAGE_KEY}.pre-v3`))
    storage.setItem(`${STORAGE_KEY}.pre-v3`, raw);
  return migrateLegacy(candidate);
}
export function saveReportingState(
  state: ReportingState,
  storage: Pick<Storage, "setItem"> = localStorage,
) {
  storage.setItem(STORAGE_KEY, JSON.stringify(state));
}
export { deleteDefinition };

const DATABASE = "luuta-generated-reports";
const STORE = "reports";
function openReports(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE, { keyPath: "id" });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(request.error ?? new Error("Unable to open saved Reports."));
    request.onblocked = () =>
      reject(new Error("Close other prototype tabs to open saved Reports."));
  });
}
async function reportTransaction<T>(
  mode: IDBTransactionMode,
  action: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openReports();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(STORE, mode),
      request = action(transaction.objectStore(STORE));
    let result: T;
    request.onsuccess = () => {
      result = request.result;
    };
    transaction.oncomplete = () => {
      db.close();
      resolve(result);
    };
    transaction.onabort = transaction.onerror = () => {
      db.close();
      reject(
        transaction.error ??
          request.error ??
          new Error(
            "Unable to save the Report. Existing Reports remain intact.",
          ),
      );
    };
  });
}
export const reportRepository = {
  async list(): Promise<GeneratedReport[]> {
    return (
      (await reportTransaction("readonly", (s) =>
        s.getAll(),
      )) as GeneratedReport[]
    ).sort((a, b) => b.generatedAt.localeCompare(a.generatedAt));
  },
  async save(report: GeneratedReport): Promise<void> {
    await reportTransaction("readwrite", (s) => s.put(structuredClone(report)));
  },
  async remove(id: string): Promise<void> {
    await reportTransaction("readwrite", (s) => s.delete(id));
  },
};
